// Pure helpers of the Connectors settings page. node --test src/lib/connectors.test.ts
import type { VaultInfo } from './api.ts';

/** The ingest address typed by an admin: '' clears the override (null), a valid http(s) URL is returned without a trailing slash, anything else is an error. */
export function parseIngestUrl(input: string): { url: string | null } | { error: string } {
  const v = input.trim();
  if (!v) return { url: null };
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return { error: 'Enter a full address such as https://vault1.example.com:8080' };
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return { error: 'The address must start with http:// or https://' };
  if (!u.hostname) return { error: 'The address needs a host name' };
  return { url: v.replace(/\/+$/, '') };
}

/** Where the address a connector uses comes from. */
export function ingestSource(n: Pick<VaultInfo, 'ingestUrl' | 'ingestUrlOverride'>): 'override' | 'reported' | 'none' {
  if (n.ingestUrlOverride) return 'override';
  return n.ingestUrl ? 'reported' : 'none';
}

/** "Last used" for a key: relative time, or that it has not been used. */
export function lastUsed(ms: number | null | undefined, now = Date.now()): string {
  if (!ms) return 'not used yet';
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

// ---------------------------------------------------------------- key scope

type ScopeLike = { kind: 'environment' | 'vaults'; vaults: readonly { name: string }[] };

/** A key that is limited to vaults but whose list is (now) empty: it sends nowhere. */
export function scopeEmpty(scope: ScopeLike): boolean {
  return scope.kind === 'vaults' && scope.vaults.length === 0;
}

/** The scope of a key row: "all vaults in Production", "helsinki-vault, tampere-vault", or why it reaches nothing. */
export function scopeLine(scope: ScopeLike, envName: string): string {
  if (scope.kind === 'environment') return `all vaults in ${envName}`;
  if (scope.vaults.length === 0) return 'reaches no vault (the vaults moved or were revoked)';
  return scope.vaults.map((v) => v.name).join(', ');
}

/** What the reveal panel says about where a new key sends, exactly. */
export function sendsToLine(scope: ScopeLike, envName: string): string {
  if (scope.kind === 'environment') return `This key sends to all vaults of ${envName}`;
  if (scope.vaults.length === 0) return 'This key sends to no vault';
  return `This key sends to: ${scope.vaults.map((v) => v.name).join(', ')}`;
}

/** Whether the scope choice can be submitted, and if not, the hint that says why. */
export function scopeCheck(mode: 'environment' | 'vaults', selected: number, vaultsInEnv: number): { ok: boolean; hint?: string } {
  if (mode === 'environment') return { ok: true };
  if (vaultsInEnv === 0) return { ok: false, hint: 'This environment has no vaults yet. Choose all vaults, or install a vault first.' };
  if (selected === 0) return { ok: false, hint: 'Choose at least one vault.' };
  return { ok: true };
}

/** A vault's view of a key that reaches it. */
export function vaultKeyScope(scope: 'environment' | 'vaults'): string {
  return scope === 'environment' ? 'whole environment' : 'this vault + others';
}

/** "<n> keys" on the Vaults list; nothing when there are none. */
export function keyCountLabel(n: number | undefined): string | null {
  if (!n || n < 1) return null;
  return n === 1 ? '1 key' : `${n} keys`;
}
